import {Injectable} from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse
} from '@angular/common/http';
import {Observable, throwError, BehaviorSubject} from 'rxjs';
import {catchError, filter, take, switchMap} from 'rxjs/operators';
import {environment} from '../../../environments/environment';
import {AuthService} from '../services';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {

  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  private publicPath = [
    `${environment.apiBaseUrl}/auth/login`,
    `${environment.apiBaseUrl}/auth/password/request`,
    `${environment.apiBaseUrl}/auth/password/reset`,
    `${environment.apiBaseUrl}/auth/refresh`,
    `${environment.apiBaseUrl}/auth/connect`,
  ];

  constructor(public authService: AuthService) {
  }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (this.publicPath.indexOf(request.url) > -1) {
      return next.handle(request);
    }
    if (this.authService.getAccessToken()) {
      request = this.addToken(request, this.authService.getAccessToken());
    }

    return next.handle(request).pipe(catchError(error => {
      if (error instanceof HttpErrorResponse && error.status === 401) {
        return this.handle401Error(request, next);
      } else {
        return throwError(error);
      }
    }));
  }

  private addToken(request: HttpRequest<any>, token: string) {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`
      }
    });
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler) {
    if (!this.isRefreshing && !this.authService.isAccessTokenExpired()) {
      return throwError('no auth');
    }
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return this.authService.refreshToken().pipe(
        switchMap((result: any) => {
          this.isRefreshing = false;
          this.authService.setSession(result);
          const accessToken = this.authService.getAccessToken();
          this.refreshTokenSubject.next(accessToken);
          return next.handle(this.addToken(request, accessToken));
        })
        , catchError((error: HttpErrorResponse) => {
          console.log('retry login');
          this.authService.redirectToLogin();
          return throwError(error.error.message);
        }));

    } else {
      return this.refreshTokenSubject.pipe(
        filter(token => token != null),
        take(1),
        switchMap(accessToken => {
          return next.handle(this.addToken(request, accessToken));
        }));
    }
  }
}
